/**
 * The machine's identity as PEOPLE refer to it: the serial number (the chassis
 * number stamped on the machine). Every department reads serials off the
 * machine itself, so that is what the UI leads with everywhere — the
 * equipment id is the internal/stored handle and only shows as a fallback
 * when a machine has no serial on record yet.
 *
 * API responses spell the fields differently depending on which controller
 * built them (serial_number / serial / machine_serial / chassis_no, and
 * equipment_id / eq_id / equipmentId), so both resolvers are tolerant: pass
 * the row straight in and they find whatever is there.
 */

const firstFilled = (obj, keys) => {
  if (!obj) return '';
  for (const k of keys) {
    const v = obj[k];
    if (v == null) continue;
    const s = String(v).trim();
    if (s && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined') return s;
  }
  return '';
};

const SERIAL_KEYS = [
  'serial_number', 'serial', 'machine_serial', 'equipment_serial',
  'chassis_number', 'chassis_no', 'serialNumber',
];

const EQUIPMENT_KEYS = ['equipment_id', 'equipmentId', 'eq_id', 'asset_id'];

const TRACKER_KEYS = ['device_id', 'deviceId', 'imei', 'tracker_id'];

/** The machine's serial number alone — '' when none is on record. */
export const machineSerial = (row) => firstFilled(row, SERIAL_KEYS);

/** The equipment id alone — '' when absent. */
export const machineEquipmentId = (row) => firstFilled(row, EQUIPMENT_KEYS);

/**
 * What to show as the machine's headline identity: the serial number, or the
 * equipment id when the serial isn't recorded, or the tracker as a last
 * resort. `fallback` (default '—') covers a row with none of the three.
 */
export const machineLabel = (row, fallback = '—') =>
  machineSerial(row) || machineEquipmentId(row) || firstFilled(row, TRACKER_KEYS) || fallback;

/**
 * The secondary line under the headline: the equipment id, but only when it
 * isn't already the headline (no point printing the same string twice).
 * Returns '' when there is nothing worth adding.
 */
export const machineSubLabel = (row) => {
  const eq = machineEquipmentId(row);
  return machineSerial(row) && eq ? eq : '';
};

/** True when this row is showing its equipment id because no serial exists. */
export const isSerialMissing = (row) => !machineSerial(row);

export default machineLabel;
